import type { TFunction } from 'i18next';

import type { PortalOverview } from '@/modules/portal/types/portalOverview';

export type PortalRenewReadinessState =
  | 'no_subscription'
  | 'payment_attention'
  | 'subscription_ended'
  | 'self_service_preparing';

export type PortalUpgradeReadinessState =
  | 'no_subscription'
  | 'access_restricted'
  | 'self_service_preparing';

const ENDED_SUBSCRIPTION_STATUSES = new Set(['cancelled', 'suspended']);

export function derivePortalRenewReadiness(overview: PortalOverview): PortalRenewReadinessState {
  if (overview.subscription_id === null) return 'no_subscription';

  if (
    overview.subscription_status !== null &&
    ENDED_SUBSCRIPTION_STATUSES.has(overview.subscription_status)
  ) {
    return 'subscription_ended';
  }

  if (
    overview.has_payment_issue ||
    overview.subscription_status === 'past_due' ||
    overview.subscription_status === 'pending_payment' ||
    overview.subscription_status === 'expired'
  ) {
    return 'payment_attention';
  }

  return 'self_service_preparing';
}

export function canPortalSelfServiceRenew(state: PortalRenewReadinessState): boolean {
  return state === 'self_service_preparing' || state === 'payment_attention';
}

export function derivePortalUpgradeReadiness(
  overview: PortalOverview,
): PortalUpgradeReadinessState {
  if (overview.subscription_id === null) return 'no_subscription';

  if (overview.system_access_status === 'blocked' || overview.system_access_status === 'limited') {
    return 'access_restricted';
  }

  return 'self_service_preparing';
}

export function formatPortalProductPlan(overview: PortalOverview): string | null {
  const parts = [overview.product_name, overview.plan_name].filter(Boolean);
  if (parts.length === 0) return null;
  return parts.join(' · ');
}

export type PortalSupportTopic = 'renew' | 'upgrade';

/** Vendor support inbox for portal "Contact support" mailto links (not tenant billing). */
export const PORTAL_VENDOR_SUPPORT_EMAIL_DEFAULT = 'support@waredpos.com';

export function resolvePortalVendorSupportEmail(): string {
  const fromEnv =
    import.meta.env['VITE_SUPPORT_EMAIL']?.trim() ||
    import.meta.env['VITE_CUSTOMER_SUPPORT_EMAIL']?.trim();
  return fromEnv || PORTAL_VENDOR_SUPPORT_EMAIL_DEFAULT;
}

export function buildPortalSupportMailto(
  _overview: PortalOverview,
  _topic: PortalSupportTopic,
  labels: { subject: string; body: string },
): string {
  const params = new URLSearchParams();
  params.set('subject', labels.subject);
  params.set('body', labels.body);

  const to = resolvePortalVendorSupportEmail();
  return `mailto:${to}?${params.toString()}`;
}

export function buildPortalSupportMailtoLabels(
  overview: PortalOverview,
  topic: PortalSupportTopic,
  t: TFunction,
): { subject: string; body: string } {
  const prefix = topic === 'renew' ? 'portal.renew.support' : 'portal.upgrade.support';
  return {
    subject: t(`${prefix}.emailSubject`, { company: overview.tenant_name }),
    body: t(`${prefix}.emailBody`, {
      company: overview.tenant_name,
      product: overview.product_name ?? '—',
      plan: overview.plan_name ?? '—',
      subscriptionId: overview.subscription_id ?? '—',
      status: overview.subscription_status ?? '—',
    }),
  };
}
